/**
 * Provider Config Context
 *
 * Loads and provides provider configuration throughout the widget application.
 * Used for white-label branding and theming.
 */

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { providerService, type ProviderConfig } from '../services/provider.service';
import { useParams } from 'react-router-dom';

interface ProviderConfigContextType {
  config: ProviderConfig | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ProviderConfigContext = createContext<ProviderConfigContextType | undefined>(undefined);

interface ProviderConfigProviderProps {
  children: ReactNode;
  providerSlug?: string; // Optional override for testing
}

/**
 * Provider Config Provider
 *
 * Fetches provider configuration on mount and makes it available via context.
 * Provider slug is extracted from URL params or passed as prop.
 */
export function ProviderConfigProvider({ children, providerSlug: overrideSlug }: ProviderConfigProviderProps) {
  const params = useParams<{ providerSlug?: string }>();
  const providerSlug = overrideSlug || params.providerSlug;

  const [config, setConfig] = useState<ProviderConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!providerSlug) {
      setError('Provider slug is required');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await providerService.getConfig(providerSlug);
      setConfig(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load provider configuration';
      setError(errorMessage);
      console.error('Failed to fetch provider config:', err);
    } finally {
      setIsLoading(false);
    }
  }, [providerSlug]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return (
    <ProviderConfigContext.Provider
      value={{
        config,
        isLoading,
        error,
        refetch: fetchConfig,
      }}
    >
      {children}
    </ProviderConfigContext.Provider>
  );
}

/**
 * Hook to use provider configuration
 *
 * @throws {Error} if used outside of ProviderConfigProvider
 */
export function useProviderConfig(): ProviderConfigContextType {
  const context = useContext(ProviderConfigContext);

  if (context === undefined) {
    throw new Error('useProviderConfig must be used within a ProviderConfigProvider');
  }

  return context;
}
